import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiJson, apiPost } from '@lib/apiClient';
import { fmtMemberId } from '@lib/member-id';
import { clientLabel, clientSortKey } from '@lib/client-key';
import { resultChipClasses } from '@lib/config-render';
import { computeReviewStats } from '@lib/review-stats';
import { runSendAll, type SendChunkResponse } from '@lib/send-all';
import {
  SIDECAR_SORT_FIELDS,
  SIDECAR_SORT_LABELS,
  sortSidecars,
  type SidecarSortField,
  type SidecarSortDir,
} from '@lib/sidecar-sort';
import { useReviewQueue, type ReviewListResponse } from '@hooks/useReviewQueue';
import { useIntakeConfig } from '@hooks/useIntakeConfig';
import { ReviewPhoto } from '@components/ReviewPhoto';
import { ReviewVoice } from '@components/ReviewVoice';
import type { IntakeConfig, SidecarRecord } from '@/types/intake';

import { useCampaign } from '@hooks/useCampaign';

const SECTIONS: { status: SidecarRecord['status']; title: string }[] = [
  { status: 'submitted', title: 'Awaiting review' },
  { status: 'returned', title: 'Returned to bench' },
  { status: 'approved', title: 'Approved' },
  { status: 'applied', title: 'Live' },
];

/**
 * Split an already-sorted list into one bucket per health plan, plan order stable and 'no plan'
 * last. Returns entries so the caller can render headings only when more than one plan is present.
 */
function groupByClient(rows: SidecarRecord[]): [string, SidecarRecord[]][] {
  const buckets = new Map<string, SidecarRecord[]>();
  for (const r of rows) {
    const key = typeof r.client === 'string' ? r.client : '';
    const list = buckets.get(key);
    if (list) list.push(r);
    else buckets.set(key, [r]);
  }
  return [...buckets.entries()].sort((a, b) =>
    clientSortKey(a[0] === '' ? null : a[0]).localeCompare(clientSortKey(b[0] === '' ? null : b[0])),
  );
}

/** The four live statuses the worklist sections cover - the active set for the stats strip. */
const SECTION_STATUSES = new Set<SidecarRecord['status']>(SECTIONS.map((s) => s.status));

/**
 * A record that has gone out via the LEGACY in-app Send (`sent`) or the older OK-to-print
 * (`printDispatch`). The in-app Send was RETIRED 2026-06-23 (the canonical handoff is now the local
 * generator's result+consent JSON feed to the Portal), so new records never get these markers; this is
 * kept only so any historically-sent record still shows a "Sent" badge and stays out of the worklist.
 */
function isSent(r: SidecarRecord): boolean {
  return !!r.sent || !!r.printDispatch;
}

/** A record still eligible for Send All: resulted (submitted/approved/applied), not returned, not yet sent. */
function isSendableRecord(r: SidecarRecord): boolean {
  return (r.status === 'submitted' || r.status === 'approved' || r.status === 'applied') && !isSent(r);
}

/**
 * Review: a second CDX user reviews resulted records - correct the result, return to the bench, or flag
 * missing consent. There is NO send step in the app anymore: resulted records are handed to the Portal
 * by the local generator (the result+consent JSON feed). This page is the QA / correction surface.
 */
export function Review() {
  const { campaign: CAMPAIGN } = useCampaign();
  const qc = useQueryClient();
  const cfg = useIntakeConfig(CAMPAIGN);
  const list = useReviewQueue(CAMPAIGN);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const [sortField, setSortField] = useState<SidecarSortField>('name');
  const [sortDir, setSortDir] = useState<SidecarSortDir>('asc');
  const [q, setQ] = useState('');

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['intake-review', CAMPAIGN] });
    void qc.invalidateQueries({ queryKey: ['intake-queue', CAMPAIGN] });
    void qc.invalidateQueries({ queryKey: ['intake-archive', CAMPAIGN] });
  };

  const items = list.data?.items ?? [];

  // Search (spec 5.3): name / tracking / member no.
  const searched = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((r) =>
      [r.memberNameSnapshot, fmtMemberId(r.memberId), r.memberId, r.kitBarcode, r.returnTracking]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(needle)),
    );
  }, [items, q]);

  const counts = {
    submitted: items.filter((r) => r.status === 'submitted' && !isSent(r)).length,
    returned: items.filter((r) => r.status === 'returned' && !isSent(r)).length,
    approved: items.filter((r) => r.status === 'approved' && !isSent(r)).length,
    applied: items.filter((r) => r.status === 'applied' && !isSent(r)).length,
  };

  // The ACTIVE (non-sent) worklist set the sections render - feed it to the stats strip so the headline
  // metrics and the cards stay in lockstep.
  const active = useMemo(
    () => items.filter((r) => SECTION_STATUSES.has(r.status) && !isSent(r)),
    [items],
  );
  const stats = useMemo(() => computeReviewStats(active), [active]);

  // Sendable = every resulted record not yet sent (submitted/approved/applied, not returned). This is the
  // set "Send All" delivers to the Portal; once sent, each record gets a `sent` marker and moves to Archive.
  const sendable = useMemo(() => items.filter(isSendableRecord), [items]);

  // One Send All press: chunk the sendable ids and POST each chunk sequentially (the server stays the
  // authoritative gate - it re-reads each sidecar under lock and skips anything already sent). Each chunk
  // delivers the result+consent JSON to the Portal and stamps `sent`, so the records leave Review for Archive.
  const postSend = async (recordIds: string[], clientRequestId: string): Promise<SendChunkResponse> => {
    const res = await apiPost('/api/intake/send', { campaign: CAMPAIGN, recordIds, clientRequestId });
    return { ok: res.ok, status: res.status, data: res.data as SendChunkResponse['data'] };
  };
  const sendAll = async () => {
    if (sending || sendable.length === 0) return;
    if (
      !window.confirm(
        `Send ${sendable.length} record(s) to the Portal? This delivers each result+consent JSON and moves them to Archive.`,
      )
    )
      return;
    const attemptedIds = sendable.map((r) => r.recordId);
    const attemptedSet = new Set(attemptedIds);
    setSending(true);
    setProgress({ done: 0, total: attemptedIds.length });
    setError(null);
    setNotice(null);
    try {
      const outcome = await runSendAll(attemptedIds, postSend, {
        baseId: crypto.randomUUID(),
        onProgress: (done, total) => setProgress({ done, total }),
      });
      // Reconcile against SERVER TRUTH, never the HTTP outcome alone: a chunk can 500 at the gateway
      // AFTER the server already delivered + marked its records (a large new-record batch can outrun the
      // ~230s Functions gateway). Re-read the live worklist and report what is ACTUALLY still sendable, so
      // a server-completed send never shows as a failure.
      let stillPending = -1;
      try {
        const fresh = await apiJson<ReviewListResponse>(
          `/api/intake/review/list?campaign=${encodeURIComponent(CAMPAIGN)}`,
        );
        stillPending = fresh.items.filter((r) => attemptedSet.has(r.recordId) && isSendableRecord(r)).length;
      } catch {
        /* reconcile fetch failed; fall back to the raw outcome.sent below */
      }
      const delivered = stillPending >= 0 ? attemptedIds.length - stillPending : outcome.sent;
      const parts = [`Delivered ${delivered} of ${attemptedIds.length}`];
      if (outcome.emailed) parts.push(`${outcome.emailed} Member Support emailed`);
      if (stillPending > 0) parts.push(`${stillPending} still pending - press Send All again`);
      setNotice(parts.join(', ') + '.');
      // Error banner ONLY when records genuinely remain unsent. A 500 the server actually completed
      // (stillPending === 0) is success, not a failure, so it must never surface as an error.
      if (stillPending > 0) {
        setError(outcome.errors.length ? `${stillPending} record(s) still pending: ${outcome.errors.join('; ')}` : null);
      } else if (stillPending < 0 && outcome.errors.length) {
        setError(`Could not verify delivery: ${outcome.errors.join('; ')}`);
      } else {
        setError(null);
      }
      refresh();
    } finally {
      setSending(false);
      setProgress(null);
    }
  };

  // Edit / return / no-consent helper: one journaled review/edit (or return) call.
  const act = async (path: string, payload: Record<string, unknown>, id: string): Promise<boolean> => {
    setBusyId(id);
    setError(null);
    setNotice(null);
    try {
      const res = await apiPost(path, { campaign: CAMPAIGN, clientRequestId: crypto.randomUUID(), recordId: id, ...payload });
      if (!res.ok) {
        setError((res.data.error as string) || `Action failed (${res.status})`);
        return false;
      }
      refresh();
      return true;
    } finally {
      setBusyId(null);
    }
  };

  if (cfg.isLoading || !cfg.data)
    return (
      <div className="li-page">
        <div className="li-state">Loading...</div>
      </div>
    );
  const config = cfg.data;

  return (
    <div className="li-page">
      <div className="li-head flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="section-eyebrow">Resulting - 26 FOBT</div>
          <h2 className="section-title">Review</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void sendAll()}
            disabled={sending || sendable.length === 0}
            className="btn-bench disabled:opacity-50"
            style={{ minHeight: 44, padding: '8px 16px', fontSize: 13 }}
            title="Deliver every resulted record to the Portal and move it to Archive"
          >
            {sending
              ? progress
                ? `Sending ${progress.done} of ${progress.total}...`
                : 'Sending...'
              : `Send All to Portal (${sendable.length})`}
          </button>
          <button
            type="button"
            onClick={refresh}
            disabled={list.isFetching}
            className="btn btn-ghost btn-sm disabled:opacity-50"
          >
            {list.isFetching ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <p className="mb-4 text-xs" style={{ color: 'var(--ink-400)', maxWidth: 760 }}>
        Review and correct resulted records here. When ready, <span className="font-semibold">Send All</span>{' '}
        delivers each record&rsquo;s result + consent to the Portal and moves it to Archive.
      </p>

      {/* Stats strip: six headline metrics over the ACTIVE set. */}
      <div className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {[
          { label: 'Positive', value: stats.pos },
          { label: 'Negative', value: stats.neg },
          { label: 'Missing consent', value: stats.missingConsent },
          { label: 'Untestable (new kit)', value: stats.untestable },
          { label: 'PCP letters', value: stats.pcpLetters },
          { label: 'Member letters', value: stats.memberLetters },
        ].map((s) => (
          <div key={s.label} className="stat" style={{ padding: '10px 12px' }}>
            <div className="value tab-num" style={{ fontSize: 22 }}>
              {s.value}
            </div>
            <div className="label" style={{ marginBottom: 0, marginTop: 2 }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Secondary lifecycle counts. */}
      <div className="mb-4 grid grid-cols-4 gap-2">
        {[
          { label: 'Awaiting', value: counts.submitted },
          { label: 'Returned', value: counts.returned },
          { label: 'Approved', value: counts.approved },
          { label: 'Live', value: counts.applied },
        ].map((s) => (
          <div key={s.label} className="stat card-sunken" style={{ padding: '7px 10px' }}>
            <div className="value tab-num" style={{ fontSize: 16 }}>
              {s.value}
            </div>
            <div className="label" style={{ marginBottom: 0, marginTop: 1 }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      <div className="li-toolbar">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, tracking, or member ID"
          className="text-input grow"
          style={{ minWidth: '12rem' }}
        />
        <label className="flex items-center gap-2">
          <span className="field-label" style={{ marginBottom: 0 }}>
            Sort
          </span>
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value as SidecarSortField)}
            className="select-input"
            style={{ width: 'auto' }}
          >
            {SIDECAR_SORT_FIELDS.map((f) => (
              <option key={f} value={f}>
                {SIDECAR_SORT_LABELS[f]}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
          className="btn btn-sm"
          title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
        >
          {sortDir === 'asc' ? 'Asc' : 'Desc'}
        </button>
      </div>

      {notice && <div className="note mb-3">{notice}</div>}
      {error && <div className="note danger mb-3">{error}</div>}
      {list.isLoading && <p className="text-sm" style={{ color: 'var(--ink-400)' }}>Loading records...</p>}
      {!list.isLoading && items.length > 0 && searched.length === 0 && (
        <p className="text-sm" style={{ color: 'var(--ink-400)' }}>No records match &ldquo;{q}&rdquo;.</p>
      )}
      {!list.isLoading && active.length === 0 && !(items.length > 0 && q.trim() && searched.length === 0) && (
        <div className="li-state">
          <p className="text-sm font-medium" style={{ color: 'var(--ink-700)' }}>No samples awaiting review</p>
          <p className="mt-1 text-xs">This refills as new samples are resulted.</p>
        </div>
      )}

      {SECTIONS.map(({ status, title }) => {
        const group = sortSidecars(
          searched.filter((r) => r.status === status && !isSent(r)),
          sortField,
          sortDir,
        );
        if (group.length === 0) return null;
        return (
          <section key={status} className="mb-6">
            <div className="block-head" style={{ marginTop: 0 }}>
              {title}
              <span className="aux tab-num">{group.length}</span>
            </div>
            <div className="space-y-4">
              {/*
                Grouped by health plan (spec 4). Grouping only, never filtering: every record in the
                section is still on screen, so a plan can never hide work from the reviewer. Records
                written before the client field carry no plan and sort last under 'Unknown plan'.
              */}
              {groupByClient(group).map(([client, rows]) => (
                <div key={client} className="space-y-4">
                  {groupByClient(group).length > 1 && (
                    <div className="block-head" style={{ marginTop: 0 }}>
                      {clientLabel(client === '' ? null : client)}
                      <span className="aux tab-num">{rows.length}</span>
                    </div>
                  )}
                  {rows.map((r) => (
                    <ReviewCard key={r.recordId} record={r} config={config} busy={busyId === r.recordId} act={act} />
                  ))}
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export function ReviewCard({
  record,
  config,
  busy,
  act,
}: {
  record: SidecarRecord;
  config: IntakeConfig;
  busy: boolean;
  act: (path: string, payload: Record<string, unknown>, id: string) => Promise<boolean>;
}) {
  const { campaign: CAMPAIGN } = useCampaign();
  const [editing, setEditing] = useState(false);
  // Optimistic no-consent state. The checkbox is bound to the server value, so a controlled checkbox
  // "snaps back" while the edit round-trips - track the intended value locally, reconcile when the
  // refetched record lands, and revert only if the edit fails. This makes no-consent reversible.
  const [ncPending, setNcPending] = useState<boolean | null>(null);

  const sent = isSent(record);
  const noConsent = ncPending ?? !!record.noConsentForm;
  useEffect(() => setNcPending(null), [record.noConsentForm]);

  const onReturn = () => {
    const note = window.prompt('Reason for returning to the bench:');
    if (note && note.trim()) void act('/api/intake/review/return', { note: note.trim() }, record.recordId);
  };
  const onEditResult = (result: string) => void act('/api/intake/review/edit', { field: 'result', to: result }, record.recordId);
  const onEditReason = () => {
    const reason = window.prompt('Reason / note:', record.reason ?? '');
    if (reason != null) void act('/api/intake/review/edit', { field: 'reason', to: reason }, record.recordId);
  };
  const onEditCollection = () => {
    const d = window.prompt('Collection date (YYYY-MM-DD), blank to clear:', record.collectionDate ?? '');
    if (d != null) void act('/api/intake/review/edit', { field: 'collectionDate', to: d.trim() }, record.recordId);
  };
  // Persistent No-Consent checkbox (spec 5.7): a REVERSIBLE toggle via a journaled edit. The consent
  // status rides the result+consent JSON to the Portal (consentOnFile); flip optimistically so an
  // accidental tick can be undone with a second tap; revert the optimistic flip if the server rejects.
  const onToggleNoConsent = async () => {
    const next = !noConsent;
    setNcPending(next);
    const oked = await act('/api/intake/review/edit', { field: 'noConsentForm', to: next }, record.recordId);
    if (!oked) setNcPending(null);
  };

  const arrival =
    record.receivedAt && record.receivedBasis !== 'override' ? record.receivedAt : 'kit in hand (no scan)';

  return (
    <div className="card" style={noConsent ? { borderColor: 'var(--amber)', background: 'var(--amber-bg)' } : undefined}>
      <div className="flex flex-col gap-4 lg:flex-row">
        {/* LEFT: the photo(s) shown LARGE for at-a-glance review. */}
        <div className="lg:w-2/3 lg:flex-shrink-0">
          {record.photos.length > 0 ? (
            <div className="space-y-3">
              {record.photos.map((p, i) => (
                <div key={p}>
                  <div className="micro" style={{ marginBottom: 4 }}>
                    Photo {i + 1} of {record.photos.length}
                  </div>
                  <ReviewPhoto campaign={CAMPAIGN} recordId={record.recordId} index={i} size="large" />
                </div>
              ))}
            </div>
          ) : (
            <div className="li-state flex h-64 items-center justify-center">No photos on this record</div>
          )}

          {record.voiceNotes && record.voiceNotes.length > 0 && (
            <div className="mt-3">
              <div className="micro" style={{ marginBottom: 4 }}>Voice notes ({record.voiceNotes.length})</div>
              <div className="space-y-2">
                {record.voiceNotes.map((p, i) => (
                  <ReviewVoice key={p} campaign={CAMPAIGN} recordId={record.recordId} index={i} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: the values to compare against the image, then the controls. */}
        <div className="min-w-0 lg:w-1/3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-lg font-semibold text-cdx-fg-strong">{record.memberNameSnapshot || '(no name)'}</div>
              <div className="text-sm" style={{ color: 'var(--ink-400)' }}>
                {clientLabel(record.client)} - member {fmtMemberId(record.memberId)} - kit{' '}
                <span className="font-mono">{record.kitBarcode}</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span className={resultChipClasses(config, record.result)}>{record.result}</span>
              {sent && <span className="pill pill-success">Sent</span>}
            </div>
          </div>

          <div className="mt-2 space-y-0.5 text-sm" style={{ color: 'var(--ink-600)' }}>
            <div>Arrival {arrival}</div>
            <div>Resulted {record.resultedAt}</div>
            {record.reason && <div>Reason: {record.reason}</div>}
            {record.collectionDate && <div>Collection date: {record.collectionDate}</div>}
            {record.returnTracking && (
              <div>
                Tracking <span className="font-mono">{record.returnTracking}</span>
              </div>
            )}
            {record.edits.length > 0 && <div>Edits: {record.edits.length}</div>}
            {record.status === 'applied' && record.appliedAt && (
              <div style={{ color: 'var(--emerald)' }}>Live since {record.appliedAt}</div>
            )}
            {record.reviewNote && <div style={{ color: 'var(--amber)' }}>Return note: {record.reviewNote}</div>}
          </div>

          {/* Persistent No-Consent control (spec 5.7) - reversible optimistic toggle, any un-sent record. */}
          {!sent && (
            <label className="note warn mt-3 flex cursor-pointer items-start gap-3" style={{ display: 'flex' }}>
              <input
                type="checkbox"
                checked={noConsent}
                disabled={busy}
                onChange={() => void onToggleNoConsent()}
                className="mt-0.5 h-5 w-5 shrink-0 accent-[color:var(--amber)]"
              />
              <span style={{ color: 'var(--amber)' }}>
                No consent form on file
                <span className="mt-0.5 block text-[11px]">
                  Recorded as the consent status sent to the Portal; Member Support follows up.
                  {noConsent ? ' Ticked by mistake? Tap again to undo.' : ''}
                </span>
              </span>
            </label>
          )}

          {/* Edit + Return - kept for any un-sent record. */}
          {!sent && (
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--rule-light)' }}>
              {editing ? (
                <div className="space-y-2">
                  <div className="micro">Correct the result</div>
                  <div className="flex flex-wrap gap-2">
                    {config.results.map((o) => (
                      <button
                        key={o.result}
                        type="button"
                        disabled={busy}
                        onClick={() => onEditResult(o.result)}
                        className={`${resultChipClasses(config, o.result)} ${o.result === record.result ? 'ring-2 ring-cdx-fg-strong/40' : ''}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" disabled={busy} onClick={onEditReason} className="btn btn-sm">
                      Edit reason
                    </button>
                    <button type="button" disabled={busy} onClick={onEditCollection} className="btn btn-sm">
                      Edit collection date
                    </button>
                    <button type="button" onClick={() => setEditing(false)} className="btn btn-sm">
                      Done editing
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    disabled={busy}
                    className="btn flex-1 justify-center disabled:opacity-50"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={onReturn}
                    disabled={busy}
                    className="btn flex-1 justify-center disabled:opacity-50"
                    style={{ borderColor: 'var(--amber)', color: 'var(--amber)' }}
                  >
                    Return
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
